import type { DataField, CascadeNode, NodeType, ChoiceOption, CaptureMethodType } from './types'

// ─── Default Data Fields ───────────────────────────────────────────────

export const DEFAULT_DATA_FIELDS: DataField[] = [
  { id: 'first_name',  label: 'First Name',   enabled: true,  fuzziness: 'moderate', captureMethod: 'user-input' },
  { id: 'middle_name', label: 'Middle Name',   enabled: false, fuzziness: 'fuzzy',    captureMethod: 'user-input' },
  { id: 'last_name',   label: 'Last Name',     enabled: true,  fuzziness: 'moderate', captureMethod: 'user-input' },
  { id: 'dob',         label: 'Date of Birth', enabled: true,  fuzziness: 'strict',   captureMethod: 'user-input' },
  { id: 'address',     label: 'Address',       enabled: true,  fuzziness: 'fuzzy',    captureMethod: 'user-input' },
  { id: 'email',       label: 'Email',         enabled: false, fuzziness: 'strict',   captureMethod: 'user-input' },
  { id: 'phone',       label: 'Phone',         enabled: false, fuzziness: 'strict',   captureMethod: 'user-input' },
  { id: 'national_id', label: 'National ID',   enabled: false, fuzziness: 'strict',   captureMethod: 'user-input' },
  { id: 'doc_scan',    label: 'Document Scan', enabled: false, fuzziness: 'strict',   captureMethod: 'ocr' },
  { id: 'selfie',      label: 'Selfie',        enabled: false, fuzziness: 'strict',   captureMethod: 'user-input' },
]

// ─── Available Verification Sources ────────────────────────────────────
// These are selectable vendors within a Validation node's detail panel.

export interface VerificationSource {
  id: string
  name: string
  shortName: string
  category: 'gov-id' | 'bureau' | 'doc-scan' | 'aml'
  vendor: string
  description: string
  verifies: string[]
  requiresConsent: boolean
  countries: string[] // ISO codes, empty = global
  price?: string
}

export const VERIFICATION_SOURCES: VerificationSource[] = [
  // Australia
  { id: 'au_dvs', name: 'DVS (Document Verification Service)', shortName: 'DVS', category: 'gov-id', vendor: 'DVS', description: 'Australian Government ID verification — Passport, DL, Birth Cert, Medicare, Visa', verifies: ['First Name', 'Last Name', 'DOB', 'National ID'], requiresConsent: true, countries: ['AU'], price: '$0.72' },
  { id: 'au_vevo', name: 'VEVO (Visa Entitlement Verification)', shortName: 'VEVO', category: 'gov-id', vendor: 'VEVO', description: 'Visa and immigration status check', verifies: ['First Name', 'Last Name', 'DOB', 'National ID'], requiresConsent: true, countries: ['AU'], price: '$1.56' },
  { id: 'au_experian', name: 'Experian Australia', shortName: 'Experian', category: 'bureau', vendor: 'Experian', description: 'Credit bureau eKYC — Name, DOB, Address from credit file', verifies: ['First Name', 'Last Name', 'DOB', 'Address'], requiresConsent: true, countries: ['AU'], price: '$1.85' },
  { id: 'au_equifax', name: 'Equifax Australia', shortName: 'Equifax', category: 'bureau', vendor: 'Equifax', description: 'Credit bureau eKYC — Name, DOB, Address from credit file', verifies: ['First Name', 'Last Name', 'DOB', 'Address'], requiresConsent: true, countries: ['AU'], price: '$1.24' },
  { id: 'au_superchoice', name: 'Superchoice', shortName: 'Superchoice', category: 'bureau', vendor: 'Superchoice', description: 'Superannuation verification — Name, DOB', verifies: ['First Name', 'Last Name', 'DOB'], requiresConsent: false, countries: ['AU'], price: '$1.00' },

  // New Zealand
  { id: 'nz_centrix_govid', name: 'Centrix GovID', shortName: 'Centrix GovID', category: 'gov-id', vendor: 'Centrix', description: 'NZ government database — NZTA Driver Licence, DIA Passport', verifies: ['First Name', 'Last Name', 'DOB', 'National ID'], requiresConsent: false, countries: ['NZ'] },
  { id: 'nz_centrix_bureau', name: 'Centrix Bureau (SmartID)', shortName: 'Centrix Bureau', category: 'bureau', vendor: 'Centrix', description: 'Multiple databases for Name, DOB, Address (each a discrete source)', verifies: ['First Name', 'Last Name', 'DOB', 'Address'], requiresConsent: false, countries: ['NZ'] },
  { id: 'nz_equifax', name: 'Equifax NZ Bureau (ID Plus)', shortName: 'Equifax NZ', category: 'bureau', vendor: 'Equifax', description: 'Multiple databases for Name, DOB, Address', verifies: ['First Name', 'Last Name', 'DOB', 'Address'], requiresConsent: false, countries: ['NZ'] },

  // Global / Multi-country eKYC vendors
  { id: 'gdc', name: 'GDC (Global Data Consortium)', shortName: 'GDC', category: 'bureau', vendor: 'GDC', description: 'International multi-source eKYC — Name, DOB, Address', verifies: ['First Name', 'Last Name', 'DOB', 'Address'], requiresConsent: false, countries: [] },
  { id: 'datazoo', name: 'Data Zoo', shortName: 'Data Zoo', category: 'bureau', vendor: 'Data Zoo', description: 'International eKYC with strong APAC coverage', verifies: ['First Name', 'Last Name', 'DOB', 'Address', 'National ID'], requiresConsent: false, countries: [] },
  { id: 'socure', name: 'Socure', shortName: 'Socure', category: 'bureau', vendor: 'Socure', description: 'International eKYC — wide country coverage', verifies: ['First Name', 'Last Name', 'DOB', 'Address', 'National ID'], requiresConsent: false, countries: [] },
  { id: 'asiaverify', name: 'Asiaverify', shortName: 'Asiaverify', category: 'gov-id', vendor: 'Asiaverify', description: 'APAC Gov ID verification — facial/liveness matching', verifies: ['First Name', 'Last Name', 'DOB', 'National ID'], requiresConsent: false, countries: ['CN', 'HK', 'ID', 'MY', 'PH', 'TH', 'VN', 'SG', 'TW', 'KR', 'JP', 'IN'] },

  // Canada
  { id: 'ca_equifax', name: 'Equifax Canada', shortName: 'Equifax CA', category: 'bureau', vendor: 'Equifax', description: 'FINTRAC Single Credit File Method', verifies: ['First Name', 'Last Name', 'DOB', 'Address'], requiresConsent: true, countries: ['CA'], price: 'CAD $2.08' },

  // AML
  { id: 'aml_comply', name: 'ComplyAdvantage', shortName: 'ComplyAdvantage', category: 'aml', vendor: 'ComplyAdvantage', description: 'PEP, Sanctions & Adverse Media screening', verifies: ['AML'], requiresConsent: false, countries: [] },
  { id: 'aml_refinitiv', name: 'Refinitiv WorldCheck', shortName: 'Refinitiv', category: 'aml', vendor: 'Refinitiv', description: 'PEP, Sanctions & Adverse Media screening', verifies: ['AML'], requiresConsent: false, countries: [] },

  // Doc Scan / IDV
  { id: 'onfido', name: 'Onfido', shortName: 'Onfido', category: 'doc-scan', vendor: 'Onfido', description: 'Document verification + facial biometrics', verifies: ['Document', 'Selfie'], requiresConsent: false, countries: [] },
  { id: 'jumio', name: 'Jumio', shortName: 'Jumio', category: 'doc-scan', vendor: 'Jumio', description: 'Document verification + facial biometrics', verifies: ['Document', 'Selfie'], requiresConsent: false, countries: [] },
  { id: 'incode', name: 'Incode', shortName: 'Incode', category: 'doc-scan', vendor: 'Incode', description: 'Document verification + facial biometrics', verifies: ['Document', 'Selfie'], requiresConsent: false, countries: [] },
]

// ─── Choice Tree ───────────────────────────────────────────────────────
// Max 4 options at every level. Top-level: Input, Check, Output.
// Each expands to max 4 sub-choices before creating a node.

// ─── Input sub-choices ────────────────────────────────────────────────
const INPUT_CHILDREN: ChoiceOption[] = [
  {
    id: 'input_form',
    label: 'Form',
    sublabel: 'Text fields & inputs',
    icon: '📝',
    nodeType: 'capture-method',
    captureMethodType: 'form' as CaptureMethodType,
    description: 'Structured form fields — name, DOB, address, etc.',
  },
  {
    id: 'input_id',
    label: 'ID Document',
    sublabel: 'Passport, licence scan',
    icon: '🪪',
    nodeType: 'capture-method',
    captureMethodType: 'id-verification' as CaptureMethodType,
    description: 'ID document capture via OCR or manual upload',
  },
  {
    id: 'input_biometric',
    label: 'Biometric',
    sublabel: 'Selfie / liveness',
    icon: '🤳',
    nodeType: 'capture-method',
    captureMethodType: 'selfie' as CaptureMethodType,
    description: 'Selfie or liveness check for biometric verification',
  },
  {
    id: 'input_data',
    label: 'Data',
    sublabel: 'API, records, enrichment',
    icon: '📡',
    nodeType: 'capture-method',
    captureMethodType: 'data' as CaptureMethodType,
    description: 'Data from API lookup, existing records, or enrichment',
  },
]

// ─── Check sub-choices ────────────────────────────────────────────────
const CHECK_CHILDREN: ChoiceOption[] = [
  {
    id: 'check_identity',
    label: 'Identity',
    sublabel: 'eKYC, doc & biometric',
    icon: '🏛️',
    nodeType: 'validation',
    description: 'Identity verification — eKYC, document check, or biometric match',
  },
  {
    id: 'check_aml',
    label: 'AML',
    sublabel: 'PEP, sanctions, media',
    icon: '🔍',
    nodeType: 'aml-screening',
    description: 'AML screening — PEP, sanctions, adverse media',
  },
  {
    id: 'check_fraud',
    label: 'Fraud',
    sublabel: 'Device, behavioral, velocity',
    icon: '🚨',
    nodeType: 'fraud-check',
    description: 'Fraud detection — device fingerprint, behavioral analysis, velocity checks',
  },
  {
    id: 'check_risk',
    label: 'Risk',
    sublabel: 'Scoring & rules engine',
    icon: '📊',
    nodeType: 'risk-check',
    description: 'Risk assessment — risk scoring, rules engine, thresholds',
  },
]

// ─── Output sub-choices ───────────────────────────────────────────────
const OUTPUT_CHILDREN: ChoiceOption[] = [
  {
    id: 'output_pass',
    label: 'Pass',
    icon: '✅',
    nodeType: 'decision',
    description: 'Approved — customer passed',
  },
  {
    id: 'output_fail',
    label: 'Fail',
    icon: '❌',
    nodeType: 'decision',
    description: 'Rejected — customer failed',
  },
  {
    id: 'output_review',
    label: 'Manual Review',
    sublabel: 'Needs analyst review',
    icon: '👀',
    nodeType: 'decision',
    description: 'Escalated for manual analyst review',
  },
]

// ─── Top-level choices (used by all node types) ───────────────────────
const STANDARD_CHOICES: ChoiceOption[] = [
  {
    id: 'cat_input',
    label: 'Input',
    sublabel: 'Capture data',
    icon: '📥',
    nodeType: 'capture-method', // placeholder — children override
    children: INPUT_CHILDREN,
  },
  {
    id: 'cat_check',
    label: 'Check',
    sublabel: 'Verify or screen',
    icon: '🛡️',
    nodeType: 'validation', // placeholder — children override
    children: CHECK_CHILDREN,
  },
  {
    id: 'cat_output',
    label: 'Output',
    sublabel: 'Decision / outcome',
    icon: '📤',
    nodeType: 'decision', // placeholder — children override
    children: OUTPUT_CHILDREN,
  },
]

/**
 * Get the contextual choices for a given node type.
 * Every node gets the same 3 top-level categories (max 4 sub-choices each).
 */
export function getChoicesForNode(nodeType: NodeType): ChoiceOption[] {
  switch (nodeType) {
    case 'customer-input':
    case 'capture-method':
    case 'validation':
    case 'aml-screening':
    case 'fraud-check':
    case 'risk-check':
    case 'decision':
      return STANDARD_CHOICES
    case 'fallback':
      return []
    default:
      return []
  }
}

// ─── Ruleset Templates ─────────────────────────────────────────────────

export interface RulesetTemplate {
  id: string
  label: string
  description: string
  shortCode: string
}

export const RULESET_TEMPLATES: RulesetTemplate[] = [
  { id: '1+1', label: '1+1', description: '1 name, 1 additional (DOB or Address)', shortCode: '1+1' },
  { id: '2+2', label: '2+2', description: '2 name, 2 additional (DOB + Address)', shortCode: '2+2' },
  { id: '2-1-1', label: '2-1-1', description: '2 name, 1 DOB, 1 address', shortCode: '2-1-1' },
  { id: '2-1-1-govid', label: '2-1-1 + GovID', description: '2 name, 1 DOB, 1 address + Government ID', shortCode: '2-1-1+G' },
  { id: 'custom', label: 'Custom', description: 'Custom ruleset configuration', shortCode: 'Custom' },
]

// ─── Helper: get sources available for a country ───────────────────────

export function getSourcesForCountry(countryCode: string): VerificationSource[] {
  return VERIFICATION_SOURCES.filter(
    s => s.countries.length === 0 || s.countries.includes(countryCode)
  )
}

// ─── Helper: create a default cascade node ─────────────────────────────

let _nextId = 1

export function createNode(
  type: NodeType,
  label: string,
  x: number,
  y: number,
  extra?: Partial<CascadeNode>
): CascadeNode {
  return {
    id: `node_${_nextId++}`,
    type,
    label,
    x,
    y,
    ...extra,
  }
}

export function resetNodeIds() {
  _nextId = 1
}

// ─── Default starting cascade ──────────────────────────────────────────

export function getDefaultCascade(): { nodes: CascadeNode[]; edges: string[][] } {
  resetNodeIds()
  const customer = createNode('customer-input', 'Customer provides their details', 0, 0)
  return {
    nodes: [customer],
    edges: [],
  }
}
